/**
 * tm/pages/CustomerPage.tsx — 고객 페이지 /c/<token> (2026-10-12 · 웹 시안 3판 · 7곳 검수 반영)
 *  로그인 없음 · 한 업체 · 보기 위주. 대표님이 하는 일 = 회신(카톡 · 문자) · 자료 올리기(자료수집 고객 포털 /portal/co/<portal_token> 그대로)
 *  사무소 이름 · 로고 없음 — 「임지도사」 · 전화 버튼 없음 · 맨 위 연락처 · 숫자 줄 없음(지도사 2026-10-12)
 *  모바일: 첫 화면 → 업무 자세히 · PC(1024+): 왼쪽 할 일 · 상담 · 안내 / 오른쪽 업무마다 카드(단계 펼침)
 */
import { PREVIEW, PreviewBar } from '../portal-ui/preview';   // 미리 보기 띠 · 주소 복사(2026-10-28)
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AppBar, AsOf, Asset, Badge, Button, Divider, G, Group, Icon, JIDO, MoreText, clamp, ListHeader, ListRow, NAVY, FILL, FoldFooter, Para, BrandStrip, setBrand, ProgressBar, StepRow, TEAL, TaskSummary, TermsBox, RoundResultBox, TextLink, Top,
  taskIcon, usePortalStyles, useToast, useWide,
} from '../portal-ui/tds';
import { AskButtons, AskFooter, useContact } from '../portal-ui/contact';
import CustomerCollect from './CustomerCollect';
import type { Contact } from '../portal-ui/contact';
import {
  asOfLabel, compareUrgency, d10, daysBetween, isEnded, kstToday, leftBadge, md, mdLong, nowLine, progressOf, stepDate, stepState, stepNote, nowSentence, taskBadge,
  short, stageText, urgency, waitBadgeText, waitsOn, roundClosedText,
  taskNews,
} from '../portal-ui/portalLogic';
import type { PStep, PTask } from '../portal-ui/portalLogic';

interface Collect { token: string; total: number; done: number; checking: number; redo: number; todo: number; redo_items: string[]; deadline: string | null; task_id: string | null; task_type: string; tasks?: { id: string; type: string; left: number }[] }
interface Consult { id: string; date: string; time: string; location: string; topic: string; with_fp: boolean }
interface Notice { id: string; content: string; created_at: string }
interface Introducer { name: string; title: string; org?: string; phone?: string; color: string | null }
interface ClientData { company: { name: string; ceo: string }; introducer?: Introducer | null; practitioner?: string; contact: Contact; as_of: string; tasks: PTask[]; consultations: Consult[]; notices: Notice[]; collect: Collect | null }

interface Todo { key: string; kind: 'collect' | 'reply' | 'checking'; task: PTask | null; step: PStep | null; title: string; sub: string; badge: ReturnType<typeof leftBadge> | null; body: string; items?: string[] }   // items = 보내 주실 서류(2026-10-28)

export default function CustomerPage({ token }: { token: string }) {
  usePortalStyles();
  const wide = useWide();
  const [data, setData] = useState<ClientData | null>(null);
  const [error, setError] = useState('');
  const [view, setView] = useState<string | null>(null);   // 업무 id(모바일 자세히)
  const [showEnded, setShowEnded] = useState(false);
  const [fold, setFold] = useState<Record<string, boolean>>({});   // PC 업무 카드 단계 — false 면 펼침(처음엔 접음 · 2026-10-27)
  const [collectOpen, setCollectOpen] = useState(false);   // 자료 보내기 — 같은 탭 · 같은 모양(2026-10-13 지도사 「고객 사이트에서 열 때만」 · 예전 자료수집 화면은 그대로)
  const [ver, setVer] = useState(0);                       // 자료를 보내고 돌아오면 진행 안내 숫자를 다시 읽는다
  const [toastNode, toast] = useToast();
  const [resend, setResend] = useState<Record<string, boolean>>({});   // 답을 보낸 뒤 「다시 보내기」를 눌러 버튼을 다시 연 단계

  useEffect(() => {
    fetch(`/api/tm/client-portal/${encodeURIComponent(token)}${PREVIEW ? '?preview=1' : ''}`)
      .then(r => r.json())
      .then((j: { success?: boolean; data?: ClientData; error?: string }) => { if (j.success && j.data) setData(j.data); else setError('이 주소는 이제 쓰지 않아요. 받으신 문자나 카카오톡 중 가장 최근 주소로 열어 주세요'); })   // 팀장님이 보낸 주소일 수도 있다(2026-10-24 페르소나 시험)
      .catch(() => setError('잠시 연결이 안 돼요. 조금 뒤 다시 열어 주세요'));
  }, [token, ver]);

  useEffect(() => { if (data) document.title = `${data.company.name.replace(/\(주\)|주식회사/g, '').trim()} 진행 안내`; }, [data]);
  useEffect(() => { window.scrollTo(0, 0); }, [view, collectOpen]);
  useEffect(() => {
    const on = () => { setView(null); setCollectOpen(open => { if (open) setVer(v => v + 1); return false; }); };
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  const openTask = (id: string) => { try { window.history.pushState({ c: id }, ''); } catch { /* 기록 실패해도 화면은 열림 */ } setView(id); };
  const closeTask = () => { if (window.history.state && (window.history.state as { c?: string }).c) window.history.back(); else setView(null); };
  // 답하기를 눌렀다는 것을 이 기기에 기억 — 다시 열면 「보내셨어요」(늘어나는 「답 기다린 지 n일」에 다시 답하지 않게)
  const sentKey = `roum-sent-${token}`;
  const [sent, setSent] = useState<Record<string, string>>(() => { try { return JSON.parse(localStorage.getItem(sentKey) || '{}') as Record<string, string>; } catch { return {}; } });
  const markSent = (stepId: string, via: 'kakao' | 'sms' | 'button') => {   // button = 대출 직접 접수 「접수했어요」(2026-10-30)
    if (PREVIEW) { toast('미리 보기에서는 답한 것으로 남기지 않아요'); return; }   // 팀장님이 미리 볼 때 대표님이 답한 것처럼 적히지 않게
    // 서버에도 남김 — 지도사 화면에 「답 왔대요」 · 팀장님 화면에 「답 확인 중」(실패해도 이 기기 표시는 그대로)
    void fetch(`/api/tm/client-portal/${encodeURIComponent(token)}/replied`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stepId, via }) }).catch(() => undefined);
    const next = { ...sent, [stepId]: kstToday() };
    setSent(next);
    try { localStorage.setItem(sentKey, JSON.stringify(next)); } catch { /* 저장 못 해도 동작은 그대로 */ }
  };

  const contact = useContact(data ? data.contact : null, toast);
  const today = kstToday();

  const active = useMemo(() => (data ? data.tasks.filter(t => !isEnded(t)) : [])
    .map(t => ({ t, u: urgency(t, 'customer', today) })).sort((a, b) => compareUrgency(a.u, b.u)).map(x => x.t), [data, today]);
  const ended = useMemo(() => (data ? data.tasks.filter(isEnded) : []), [data]);

  const todos = useMemo<Todo[]>(() => {
    if (!data) return [];
    const out: Todo[] = [];
    const c = data.collect;
    if (c && c.todo + c.redo > 0) {
      const n = c.todo + c.redo;
      out.push({
        key: 'collect', kind: 'collect', task: data.tasks.find(t => t.id === c.task_id) || null, step: null,
        title: `자료 ${n}개를 보내 주세요`,
        // 한 줄 한 뜻 — 사업 이름 먼저 · 다시 보낼 자료는 조사 없이(「…」는 → 이름에 따라 틀렸음) · 2026-10-13 검수
        sub: [c.task_type || '자료 제출', c.redo ? `다시 보낼 자료 ${c.redo_items && c.redo_items[0] ? `「${c.redo_items[0]}」${c.redo > 1 ? ` 외 ${c.redo - 1}개` : ''}` : `${c.redo}개`}` : ''].filter(Boolean).join(' · '),
        badge: c.deadline ? { ...leftBadge(daysBetween(today, c.deadline)), text: daysBetween(today, c.deadline) < 0 ? leftBadge(daysBetween(today, c.deadline)).text : `${md(c.deadline)}까지` } : null, body: '',
      });
    }
    if (c && c.todo + c.redo === 0 && c.checking > 0) {
      out.push({ key: 'checking', kind: 'checking', task: null, step: null, title: `보내 주신 자료 ${c.checking}개를 ${JIDO}가 보고 있어요`, sub: c.task_type || '자료 제출', badge: null, body: '' });
    }
    // 자료 받기 단계(collect)는 「답하기」가 아니다 — 위 「자료 n개를 보내 주세요」 한 줄이 맡는다(2026-10-27 「필요자료 취합 — 답해 주세요」)
    data.tasks.filter(t => !isEnded(t)).forEach(t => t.steps.filter(s => waitsOn(s, 'customer') && !s.collect).forEach(s => {
      const days = Math.max(0, daysBetween(d10(s.wait_since) || today, today));
      out.push({
        key: s.id, kind: 'reply', task: t, step: s,
        title: s.name, sub: t.type,
        // 보낸 뒤에는 배지 없이 아래 「✓ … 보내셨어요」 한 줄만(같은 말 두 번 ✕ · 2026-10-26)
        badge: (sent[s.id] && sent[s.id] >= (d10(s.wait_since) || '')) || s.claimed_by ? null : { text: waitBadgeText(days, 'customer'), tone: 'teal' },
        body: s.wait_question || (s.wait_items && s.wait_items.length ? '아래 서류를 보내 주세요' : `${JIDO}가 보내 드린 내용을 보시고 답해 주세요`),
        items: s.wait_items || [],   // 이미 받은 서류는 서버가 뺐다(2026-10-28 지도사 「신용정보조회서는 기초정보 수집할 때 받았어」)
      });
    }));
    return out;
  }, [data, today, sent]);

  if (error) return <Shell><div style={{ padding: '120px 32px', textAlign: 'center' }}><Para>{error}</Para></div></Shell>;
  if (!data) return <Shell><div style={{ padding: '120px 32px', textAlign: 'center', color: G.g600, fontSize: 16 }}>불러오는 중이에요</div></Shell>;

  const company = data.company.name;
  // 소개한 팀장님 — 이름과 소속 보험사 색만(보험사 이름은 안 씀 · 2026-10-13 지도사 「소개자 이름 · 보험사 팀장님 색상으로」)
  const intro = data.introducer || null;
  setBrand(intro ? intro.color : null);
  const fromLine = intro ? `${short(intro.name, 6)} ${intro.title}님 소개, ${JIDO} 진행 안내` : `${JIDO}가 보낸 진행 안내예요`;   // 휴대폰 한 줄에 들어가게(「…진행 안…」으로 잘렸음)
  const ctx = (extra: string) => `${company}${extra ? ', ' + extra : ''}`;
  // 자료 올리기 → 자료 포털 위에 「진행 안내로 돌아가기」가 뜨도록 돌아올 주소를 남김
  // 자료 보내기 — 같은 탭 안에서 진행 안내와 같은 모양으로 연다(새 창 · 다른 사이트 모양 없음) · 휴대폰 뒤로 = 진행 안내(2026-10-13 지도사)
  // 팀장님이 보낸 진행 링크에는 자료 올리기 주소가 없다(token '' · 2026-10-24) — 그때는 할 일만 보이고 보내기는 지도사가 보낸 주소에서
  const canCollect = !!(data.collect && data.collect.token);
  const goCollect = () => { if (!data.collect || !data.collect.token) return; try { window.history.pushState({ cc: 1 }, ''); } catch { /* 기록 실패해도 화면은 열림 */ } setView(null); setCollectOpen(true); };
  const closeCollect = () => { if (window.history.state && (window.history.state as { cc?: number }).cc) window.history.back(); else { setCollectOpen(false); setVer(v => v + 1); } };
  const asof = asOfLabel(data.as_of);
  const realTodos = todos.filter(td => td.kind === 'collect' || (td.kind === 'reply' && !!td.badge));   // 이미 답을 보낸 것은 셈에서 뺀다(배지 없음 = 보냄 · 2026-10-26)
  const headline = realTodos.length
    ? `대표님이 하실 일이 ${realTodos.length}개 있어요`
    : active.length ? '지금 대표님이 하실 일은 없어요' : '맡기신 일이 모두 끝났어요';   // 2026-10-26 「너무 산만하지 않나」 — 할 일이 없으면 그 한 줄이 답(진행 상황은 아래 업무마다)
  const nextConsult = data.consultations[0] || null;
  const scrollToTodo = (key: string) => {
    if (!wide) setView(null);
    window.setTimeout(() => { const el = document.getElementById('todo-' + key); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 60);
  };

  /** 이 업무에 남은 대표님 자료 수 — 묶음 링크로 여러 업무 자료를 한 번에 받을 때 업무마다(서버 collect.tasks · 2026-10-27) */
  const docsLeft = (taskId: string): number => {
    const c = data.collect; if (!c) return 0;
    if (c.tasks) { const x = c.tasks.find(y => y.id === taskId); return x ? x.left : 0; }
    return c.task_id === taskId ? c.todo + c.redo : 0;   // 옛 서버
  };
  /** 지금 줄 — 자료를 기다리는 업무면 대표님 차례로 */
  const nowOf = (t: PTask): { text: string; wait: boolean; full?: boolean } => {
    const n = docsLeft(t.id);
    if (n > 0) return { text: `대표님 자료 ${n}개를 기다려요`, wait: true };
    // 자료 받기 단계인데 보낼 자료가 남지 않음 — 「대표님 답을 기다려요」가 아니다(맨 위 「하실 일 없어요」와 어긋났음 · 2026-10-27 성보메디칼)
    if (t.steps.some(s => s.collect && s.status === '회신대기')) return { text: data.collect ? `${JIDO}가 보내 주신 자료를 확인하고 있어요` : `${JIDO}가 자료를 챙기고 있어요`, wait: false };
    const w = t.steps.find(s => waitsOn(s, 'customer') && !!sent[s.id] && sent[s.id] >= (d10(s.wait_since) || ''));
    if (w) return { text: `${JIDO}가 보내 주신 답을 확인하고 있어요`, wait: false };   // 이 기기에서 답을 보냈으면(서버 표시 전에도) 「기다려요」 대신
    return nowLine(t, 'customer', JIDO);
  };

  /* ── 부품 ── */
  // 담당 — 1. 소개 팀장님(소속 · 연락처) 2. 실무(임우승 경영지도사) · 전화 버튼 없이 번호는 글자로(2026-10-13 지도사)
  const telFmt = (v: string) => { const d = v.replace(/\D/g, ''); return d.length === 11 ? `${d.slice(0, 3)}\u2011${d.slice(3, 7)}\u2011${d.slice(7)}` : v; };   // 줄바꿈 안 되는 하이픈 — 번호가 두 줄로 갈라지지 않게
  const staffGroup = (intro || data.practitioner) && (
    <>
      <ListHeader title="담당" />
      {intro && <ListRow left={<Asset ch="1" bg={FILL + '24'} />} title={`${intro.name} ${intro.title}`} sub={['소개', intro.org || '', intro.phone ? telFmt(intro.phone) : ''].filter(Boolean).join(' · ')} />}
      {intro && data.practitioner && <Divider />}
      {data.practitioner && <ListRow left={<Asset ch={intro ? '2' : '1'} />} title={data.practitioner} sub="실무 담당, 서류 준비부터 신청까지" />}
    </>
  );
  const todoGroup = todos.length > 0 && (
    <>
      <ListHeader title="지금 하실 일" />
      {todos.map((td, i) => (
        <div key={td.key} id={'todo-' + td.key}>
          {i > 0 && <Divider full />}
          <ListRow left={<Asset icon={td.kind === 'collect' || td.kind === 'checking' ? 'up' : 'clip'} />} title={td.title} sub={td.sub} badge={td.badge ? <Badge spec={td.badge} /> : undefined} pad={i > 0 ? '16px 20px 12px' : '12px 20px'} />
          {td.kind === 'checking' ? <div style={{ height: 8 }} /> : td.kind === 'collect' ? (
            canCollect
              ? <div style={{ padding: '4px 20px 16px' }}><Button onClick={goCollect}>자료 보내기</Button></div>
              : <div style={{ padding: '0 20px 16px 74px', fontSize: 15, color: G.g700, wordBreak: 'keep-all' }}>{JIDO}가 보내 드린 자료 보내기 주소에서 보내 주세요</div>
          ) : (
            <>
              {td.body && (
                <div style={{ margin: '0 20px 4px 74px', padding: '12px 14px', borderRadius: 12, background: G.g100 }}>
                  <MoreText lines={4}>{td.body}</MoreText>
                  {/* 보내 주실 서류 — 같은 글자 크기 · 한 줄에 하나(2026-10-28) */}
                  {td.items && td.items.length > 0 && (
                    <ul aria-label={td.step && td.step.self ? '준비 서류' : '보내 주실 서류'} style={{ listStyle: 'none', margin: '10px 0 0', padding: '10px 0 0', borderTop: `1px solid ${G.g200}`, display: 'grid', gap: 6 }}>
                      {td.items.map(x => <li key={x} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 16, color: G.g800, lineHeight: 1.6 }}><span aria-hidden style={{ color: G.g500 }}>·</span>{x}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {td.step && td.step.self && !(sent[td.step.id] || td.step.claimed_by) ? (
                // 대출 「대표님 직접 접수」(2026-10-30 지도사) — 온라인이면 접수하러 가기 · 접수한 뒤 「접수했어요」 · 어려우면 문의(카톡 · 문자)
                <div style={{ padding: '8px 20px 16px', display: 'grid', gap: 8 }}>
                  {td.step.self.how === 'online' && /^https?:\/\//i.test(td.step.self.url) && (
                    <a href={td.step.self.url} target="_blank" rel="noopener noreferrer" className="tds-btn"
                      style={{ height: 48, borderRadius: 14, background: FILL + '1A', color: NAVY, fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>온라인 접수하러 가기 ↗</a>
                  )}
                  <Button kind="fill" h={48} onClick={() => { if (td.step) { markSent(td.step.id, 'button'); if (!PREVIEW) toast(`고맙습니다 — ${JIDO}가 확인하고 다음 단계로 넘길게요`); } }}>접수했어요</Button>
                  <div style={{ fontSize: 15, color: G.g700, textAlign: 'center' }}>접수가 어렵거나 궁금하신 점이 있으면</div>
                  <AskButtons c={contact} ask context={ctx(`${td.sub}, ${td.step.name}`)} smsBody={`[${company}] ${td.sub} 접수 관련 문의드려요: `} />
                </div>
              ) : td.step && (sent[td.step.id] || td.step.claimed_by) && !resend[td.step.id] ? (
                // 2026-10-26 — 답한 뒤에는 버튼 둘 대신 한 줄(「다시 보내기」만 작게) · 지도사가 확인하면 이 줄도 지워진다
                <div style={{ padding: '10px 20px 6px 74px', fontSize: 15, color: G.g700, wordBreak: 'keep-all' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ flexShrink: 0, marginTop: 2 }}><Icon name="check" size={18} color={TEAL} sw={2.4} /></span>
                    <span>{td.step.claimed_by === 'FP' ? `담당 팀장님이 ${md(td.step.claimed_at)}에 답을 보냈어요.` : td.step.self ? `${md(sent[td.step.id] || td.step.claimed_at)}에 접수하셨다고 알려 주셨어요.` : `${md(sent[td.step.id] || td.step.claimed_at)}에 보내셨어요.`} {JIDO}가 확인하면 여기서 지워져요</span>
                  </div>
                  <TextLink color={G.g700} onClick={() => { const id = td.step ? td.step.id : ''; setResend(r => ({ ...r, [id]: true })); }}>다시 보내기</TextLink>
                </div>
              ) : (
                <div style={{ padding: '8px 20px 16px' }}>
                  <AskButtons c={contact} context={ctx(`${td.sub}${td.step ? ', ' + td.step.name : ''}`)} smsBody={`[${company}] ${td.sub}${td.step ? ' ' + td.step.name : ''} 건 답드려요: `} onUse={via => { if (td.step) { markSent(td.step.id, via); const id = td.step.id; setResend(r => ({ ...r, [id]: false })); } }} />
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </>
  );

  const consultGroup = nextConsult && (
    <>
      <ListHeader title="다음 상담" />
      <ListRow left={<Asset icon="cal" />} title={`${mdLong(nextConsult.date)}${nextConsult.time ? ' ' + nextConsult.time : ''}`}
        sub={[nextConsult.location, nextConsult.with_fp ? '담당 팀장님도 함께 와요' : ''].filter(Boolean).join(' · ')}
        right={<Badge spec={leftBadge(daysBetween(today, nextConsult.date), '뒤')} />} />
    </>
  );

  const noticeGroup = data.notices.length > 0 && (
    <>
      <ListHeader title={`${JIDO}의 안내`} />
      {data.notices.slice(0, 3).map((n, i) => (
        <div key={n.id}>
          {i > 0 && <Divider full />}
          <div style={{ padding: '8px 20px 14px' }}>
            <div style={{ fontSize: 15, color: G.g600, marginBottom: 4 }}>{mdLong(n.created_at)}</div>
            <MoreText lines={5}>{n.content}</MoreText>
          </div>
        </div>
      ))}
    </>
  );

  const loanLabel = (t: PTask) => String(t.type || '').replace(/^정책자금 대출\s*·\s*/, '').replace(/^정책자금 대출$/, '');
  const newsOf = (t: PTask) => taskNews(t, md);   // 접수 첫 소식은 빼고(2026-10-28)
  const taskHead = (t: PTask, arrow?: boolean) => {
    const p = progressOf(t); const nl = nowOf(t); const multi = p.total > 1;   // 한 단계짜리는 진행 막대 · 「n단계 중」 없음
    // 회신 배지는 위 「대표님이 할 일」에 이미 있다 — 진행 카드에는 다시 안 붙임(같은 말 반복 X · 2026-10-12 검수)
    const bd = t.status === '보류' ? taskBadge(t, 'customer', today) : null;
    return (
      <>
        <ListRow left={<Asset icon={taskIcon(t.type)} />} title={t.type} sub={stageText(t)} subRight={bd ? <Badge spec={bd} /> : undefined}
          arrow={arrow} onClick={arrow ? () => openTask(t.id) : undefined} />
        <TaskSummary pct={p.pct} bar={multi} now={nowSentence(nl)} wait={nl.wait} news={newsOf(t)} pad={`0 ${arrow ? 54 : 20}px 16px 74px`}>
          {t.terms && <TermsBox terms={t.terms} label={loanLabel(t)} />}
          {t.status === '중단' && t.round_result && <RoundResultBox r={t.round_result} label={loanLabel(t)} />}
        </TaskSummary>
      </>
    );
  };
  /** PC 업무 카드 — 요약 · (펼치면) 단계 · 맨 아래 펼치기 줄. 단계는 처음엔 접어 둔다(2026-10-27 지도사 「뭔가 복잡하고 어떻게 써야 되나 생각하게 되네」) */
  const taskCard = (t: PTask) => {
    const total = progressOf(t).total;
    const open = fold[t.id] === false;
    return (
      <>
        {taskHead(t)}
        {(open || total <= 1) && steps(t)}
        {total > 1 && <FoldFooter open={open} count={total} onClick={() => setFold(f => ({ ...f, [t.id]: open }))} />}
      </>
    );
  };

  const steps = (t: PTask) => {
    // 대표님 자료를 기다리는 업무 — 진행 중 단계는 「자료가 오면 이어서 해요」(맨 위 「대표님 자료를 기다려요」와 한 말 · 2026-10-13 인지공학 검수)
    const docsWait = docsLeft(t.id) > 0;
    return t.steps.map((s, i) => {
      const st = stepState(t, s);   // 결과 미○○ · 다시 신청 단계 포함(2026-10-18)
      const mine = s.status === '회신대기' && waitsOn(s, 'customer') && !s.collect;
      const docsStep = !!s.collect && s.status !== '완료' && docsWait;   // 자료 받기 단계 — 「자료 보내기」로
      const done = mine && (!!s.claimed_by || (!!sent[s.id] && sent[s.id] >= (d10(s.wait_since) || '')));
      const date: ReactNode = docsStep ? (canCollect ? <TextLink color={TEAL} onClick={goCollect}>자료 보내기</TextLink> : null)
        : mine && !done
        ? <TextLink color={TEAL} onClick={() => scrollToTodo(s.id)}>답하기</TextLink>
        : done ? <span style={{ fontSize: 15, color: G.g700 }}>확인 중</span>
        : stepDate(s, 'customer');   // 끝난 날만 — 지도사 목표일은 안 보임(2026-10-12 검수)
      return <StepRow key={s.id} n={i + 1} name={s.name} note={docsStep ? `대표님 자료 ${docsLeft(t.id)}개를 기다려요` : s.collect && s.status === '회신대기' ? (data.collect ? `${JIDO}가 보내 주신 자료를 확인하고 있어요` : `${JIDO}가 자료를 챙기고 있어요`) : docsWait && s.status === '진행' ? '대표님 자료가 오면 이어서 해요' : done ? '보내 주신 답을 확인하고 있어요' : stepNote(s, 'customer', JIDO)} date={date} state={st} />;
    });
  };

  const endedRows = ended.map(t => (
    <ListRow key={t.id} left={<Asset icon={taskIcon(t.type)} />} title={t.type} dim
      sub={t.status === '완료' ? `${md(t.done_at)} 끝났어요` : t.round_closed ? roundClosedText(t) : `${md(t.stopped_at)} 멈췄어요`} right={t.status === '완료' ? undefined : <Badge spec={taskBadge(t, 'customer', today)} />} />
  ));

  /* ── 자료 보내기(휴대폰 · PC 같은 한 줄 화면) ── */
  if (collectOpen && data.collect && data.collect.token) {
    return (
      // PC 는 진행 안내와 같은 두 칸 틀 그대로(2026-10-27 지도사 「자료 보내기 누르니까 확 좁아들면서 어색」) · 휴대폰은 한 줄
      <Shell wide={wide}>
        <CustomerCollect token={data.collect.token} deadline={data.collect.deadline} onClose={closeCollect} toast={toast} wide={wide}
          aside={wide ? <AskFooter c={contact} context={ctx('자료 보내기')} pc /> : undefined} />
        {!wide && <AskFooter c={contact} context={ctx('자료 보내기')} />}
        {contact.sheet}{toastNode}
      </Shell>
    );
  }

  /* ── PC ── */
  if (wide) {
    return (
      <Shell wide>
        {/* 맨 위 「진행 안내 · 대표님께만 보내 드린 주소예요」 줄은 뺐다(2026-10-13 지도사 「이런 문구는 필요 없지」) — 기준 시각은 첫 카드 안 */}
        <div style={{ maxWidth: 1168, margin: '0 auto', padding: '28px 24px', display: 'grid', gridTemplateColumns: '400px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <section style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', marginBottom: 12 }}>
              <Top title={`${short(company, 11)} 대표님`} subTop={fromLine} subBottom={headline} padT={22} padB={22} side={20} extra={<AsOf text={asof} mt={10} />} />
            </section>
            {todoGroup && <Group m="0 0 12px">{todoGroup}</Group>}
            {consultGroup && <Group m="0 0 12px">{consultGroup}</Group>}
            {noticeGroup && <Group m="0 0 12px">{noticeGroup}</Group>}
            {staffGroup && <Group m="0 0 12px">{staffGroup}</Group>}
            <AskFooter c={contact} context={company} pc />
          </div>
          <div style={{ minWidth: 0 }}>
            {/* 제목을 첫 카드 안에 — 왼쪽 첫 카드와 위쪽 선이 맞게(오와 열 · 2026-10-12 검수) */}
            {active.length === 0 && <Group m="0 0 12px"><ListHeader title="진행 중인 일" /><div style={{ padding: '4px 20px 20px' }}><Para color={G.g600}>지금 진행 중인 일이 없어요</Para></div></Group>}
            {active.map((t, i) => <Group key={t.id} m="0 0 12px" pad="4px 0 8px">{i === 0 && <ListHeader title="진행 중인 일" right={`${active.length}개`} />}{taskCard(t)}</Group>)}
            {ended.length > 0 && <Group m="0 0 12px"><ListHeader title="끝났거나 멈춘 일" right={`${ended.length}개`} />{endedRows}</Group>}
          </div>
        </div>
        {contact.sheet}{toastNode}
      </Shell>
    );
  }

  /* ── 모바일 · 업무 자세히 ── */
  const sel = view ? data.tasks.find(t => t.id === view) : null;
  if (sel) {
    const p = progressOf(sel);
    const myTodo = todos.find(td => td.kind === 'reply' && td.task && td.task.id === sel.id) || (docsLeft(sel.id) > 0 ? todos.find(td => td.kind === 'collect') : undefined);
    return (
      <Shell>
        <AppBar onBack={closeTask} />
        <Top title={sel.type} subTop={company} subBottom={nowSentence(nowOf(sel))} padB={12} />
        <div style={{ background: '#fff', padding: '4px 32px 20px' }}>
          {p.total > 1 && <><div style={{ fontSize: 15, color: G.g700, marginBottom: 8 }}>{stageText(sel)}</div>
          <ProgressBar pct={p.pct} label={`${sel.type} 진행`} /></>}
          <TaskSummary pct={p.pct} bar={false} news={newsOf(sel)} pad={p.total > 1 ? '14px 0 0' : '0'}>
            {sel.terms && <TermsBox terms={sel.terms} label={loanLabel(sel)} />}
            {sel.status === '중단' && sel.round_result && <RoundResultBox r={sel.round_result} label={loanLabel(sel)} />}
          </TaskSummary>
          {myTodo && (
            <button type="button" className="tds-btn" onClick={() => (myTodo.kind === 'collect' && canCollect ? goCollect() : scrollToTodo(myTodo.key))}
              style={{ marginTop: 16, width: '100%', minHeight: 48, borderRadius: 12, background: FILL + '14', display: 'flex', alignItems: 'center', padding: '8px 14px', gap: 8, fontSize: 15, color: NAVY, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span style={{ flex: 1, textAlign: 'left', wordBreak: 'keep-all', ...clamp(2) }}>할 일 <b>{myTodo.kind === 'collect' ? myTodo.title.replace('를 보내 주세요', ' 보내기') : '답하기'}</b></span>
              <Badge spec={myTodo.badge} /><Icon name="r" size={16} color={NAVY} sw={2} />
            </button>
          )}
        </div>
        <div style={{ height: 12 }} />
        <Group><ListHeader title="단계" />{steps(sel)}</Group>
        {noticeGroup && <Group>{noticeGroup}</Group>}
        <AskFooter c={contact} context={ctx(sel.type)} />
        {contact.sheet}{toastNode}
      </Shell>
    );
  }

  /* ── 모바일 · 첫 화면 ── */
  return (
    <Shell>
      <Top title={`${short(company, 11)} 대표님`} subTop={fromLine} subBottom={headline} padT={28} extra={<AsOf text={asof} />} />
      <div style={{ height: 12 }} />
      {todoGroup && <Group>{todoGroup}</Group>}
      {consultGroup && <Group>{consultGroup}</Group>}
      {active.length > 0 && (
        <Group>
          <ListHeader title="진행 중인 일" right={`${active.length}개`} />
          {active.map((t, i) => <div key={t.id}>{i > 0 && <Divider />}{taskHead(t, true)}</div>)}
        </Group>
      )}
      {noticeGroup && <Group>{noticeGroup}</Group>}
      {staffGroup && <Group>{staffGroup}</Group>}
      {ended.length > 0 && (
        <Group pad="4px 0">
          <button type="button" className="tds-row" onClick={() => setShowEnded(v => !v)} aria-expanded={showEnded}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: G.g900 }}>끝났거나 멈춘 일</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 15, fontWeight: 600, color: G.g700 }}>{ended.length}개 {showEnded ? '접기' : '펼치기'}<Icon name={showEnded ? 'u' : 'd'} size={16} sw={2} /></span>
          </button>
          {showEnded && endedRows}
        </Group>
      )}
      <AskFooter c={contact} context={company} />
      {contact.sheet}{toastNode}
    </Shell>
  );
}

/** 바탕 — 모바일은 가운데 640 한 줄, PC 는 꽉 찬 폭(안에서 1120 격자) */
/** 팀장님 미리 보기(?preview=1 · 2026-10-24 페르소나 시험) — 대표님 화면 그대로 · 맨 위에 한 줄 · 연 기록 · 답하기 기록은 남지 않음 */
function Shell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="tds-root" style={{ overflowX: 'hidden' }}>
      <PreviewBar who="대표님" />
      <BrandStrip /><div style={wide ? undefined : { maxWidth: 640, margin: '0 auto' }}>{children}</div>
    </div>
  );
}
